/* M6.4 — Reconciliation Workspace. The upgraded COD Details grid: computed
   Expected Remittance Date (hover for derivation), computed COD Status + Status
   Match, multi-select courier filter, COD-status / status-match / value-range
   filters, an "Only Pending" preset, row actions (View derivation, Raise Dispute,
   Mark Received) and bulk actions (Mark Received, Raise Disputes, Verify UTRs,
   Export Selected).

   Dashboard KPI clicks deep-link here with a status (+courier) preset; the
   "Only Pending" toolbar button applies the Due/Overdue preset. Presets are
   shown as a removable chip and applied by pre-filtering the rows handed to the
   shared table (which keeps its own search/sort/filter/paging state). */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import {
  SupplyTable,
  Badge,
  Modal,
  ConfirmDialog,
  useToast,
  inr,
  fmtDate,
  fmtDateTime,
  download,
  toCsv,
} from '../components'
import {VerticalChip, DueChip, MiniPill} from './codBits'
import RaiseDisputeDrawer from './RaiseDisputeDrawer'
import {
  visibleCodRows,
  expectedRemittance,
  codStatus,
  COD_STATUS_OPTS,
  CURRENT_USER,
  TODAY,
} from './codLogic'

function ExpectedCell({row}) {
  const er = expectedRemittance(row)
  if (er.missing)
    return (
      <span className='mini-pill mp-red' title={er.deriv}>
        Config missing
      </span>
    )
  return (
    <span title={er.deriv} style={{borderBottom: '1px dotted var(--neutral)', cursor: 'help'}}>
      {fmtDate(er.date)}
    </span>
  )
}
ExpectedCell.propTypes = {row: PropTypes.object}

const codBadgeKey = (status) =>
  ({Received: 'received', Due: 'due', Overdue: 'overdue', Disputed: 'disputed', Reversed: 'reversed'}[
    status
  ] || 'norecord')

export default function WorkspaceTab({statusSeed, courierSeed}) {
  const toast = useToast()
  const [, force] = useState(0)
  const refresh = () => force((n) => n + 1)
  const [drawer, setDrawer] = useState({open: false, rows: []})
  const [deriv, setDeriv] = useState(null)
  const [confirm, setConfirm] = useState(null)

  /* active preset: {statuses:[], courier} — seeded from dashboard or "Only Pending" */
  const [preset, setPreset] = useState(() =>
    statusSeed && statusSeed.length ? {statuses: statusSeed, courier: courierSeed} : null
  )

  const allRows = visibleCodRows()
  const courierSet = useMemo(() => {
    const set = []
    allRows.forEach((r) => {
      if (set.indexOf(r.courier) < 0) set.push(r.courier)
    })
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* rows fed to the table — pre-filtered when a preset is active */
  const rows = useMemo(() => {
    if (!preset) return allRows
    const stSet = new Set(preset.statuses || [])
    return allRows.filter((r) => {
      if (stSet.size && !stSet.has(codStatus(r))) return false
      if (preset.courier && preset.courier !== 'All' && r.courier !== preset.courier) return false
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, force])

  const doMarkReceived = (targets, done) => {
    setConfirm({
      title: 'Mark ' + targets.length + ' AWB(s) as Received?',
      okLabel: 'Mark Received',
      needReason: true,
      body: 'This records a finance-verification note against each AWB and flips Courier COD Status to Received.',
      bullets: [
        'Sets COD Status = Received',
        'Stamps your email + timestamp as the verifier',
        'Reconciliation rate recalculates immediately',
      ],
      onOk: (reason) => {
        targets.forEach((r) => {
          r.courierStatus = 'Received'
          r.merchantStatus = 'Received'
          r.due = false
          r.codStatus = 'Received'
          r._verifyNote = reason
          r._verifiedBy = CURRENT_USER
          r.receivedOn = r.receivedOn || TODAY.toISOString()
        })
        if (done) done()
        refresh()
        toast(targets.length + ' AWB(s) marked Received. Audit recorded.', 'success')
      },
    })
  }

  const columns = [
    {key: 'awb', label: 'AWB', render: (r) => <span className='mono'>{r.awb}</span>},
    {
      key: 'ref',
      label: 'Reference',
      render: (r) => (
        <span className='mono' style={{fontSize: 12}}>
          {r.ref}
        </span>
      ),
    },
    {
      key: 'cpCode',
      label: 'CP Account Code',
      render: (r) => (
        <span className='mono' style={{fontSize: 12}}>
          {r.cpCode}
        </span>
      ),
    },
    {
      key: 'courier',
      label: 'Courier Parent',
      render: (r) => (
        <>
          <b>{r.courier}</b> <VerticalChip vertical={r.vertical} />
        </>
      ),
      exportValue: (r) => r.courier,
    },
    {key: 'merchant', label: 'Merchant'},
    {key: 'orderDate', label: 'Order Date', render: (r) => fmtDate(r.orderDate), value: (r) => r.orderDate},
    {
      key: 'delivered',
      label: 'Delivered Time',
      render: (r) => fmtDateTime(r.delivered),
      value: (r) => r.delivered,
    },
    {
      key: 'proshipMarked',
      label: 'ProShip Delivered Marked',
      render: (r) => fmtDateTime(r.proshipMarked),
      value: (r) => r.proshipMarked,
    },
    {
      key: 'invoiceValue',
      label: 'Invoice Value',
      align: 'right',
      render: (r) => <span className='mono'>{inr(r.invoiceValue)}</span>,
      value: (r) => r.invoiceValue,
    },
    {
      key: 'codValue',
      label: 'COD Value',
      align: 'right',
      render: (r) => <b className='mono'>{inr(r.codValue)}</b>,
      value: (r) => r.codValue,
    },
    {
      key: 'merchantStatus',
      label: 'Merchant COD Status',
      render: (r) => <span className='muted'>{r.merchantStatus}</span>,
    },
    {key: 'courierStatus', label: 'Courier COD Status'},
    {
      key: 'courierDue',
      label: 'Courier COD Due',
      sortable: false,
      render: (r) => <DueChip owed={r.due} />,
      exportValue: (r) => (r.due ? 'true' : 'false'),
    },
    {
      key: 'expected',
      label: 'Expected Remittance Date',
      sortable: false,
      render: (r) => <ExpectedCell row={r} />,
      exportValue: (r) => {
        const er = expectedRemittance(r)
        return er.missing ? 'CONFIG MISSING' : fmtDate(er.date)
      },
    },
    {
      key: 'codStatus',
      label: 'COD Status',
      sortable: false,
      render: (r) => <Badge status={codBadgeKey(codStatus(r))} label={codStatus(r)} />,
      exportValue: (r) => codStatus(r),
    },
    {
      key: 'statusMatch',
      label: 'Status Match',
      render: (r) =>
        r.statusMatch === 'Mismatch' ? (
          <Badge status='mismatch' label='Mismatch' />
        ) : (
          <Badge status='match' label='Match' />
        ),
    },
    {
      key: 'remitted',
      label: 'Remitted',
      align: 'right',
      render: (r) =>
        r.remitted != null ? (
          r.remitted < r.codValue ? (
            <span
              className='mono'
              title={'Short-paid vs COD ' + inr(r.codValue)}
              style={{color: 'var(--error)', fontWeight: 600}}
            >
              {inr(r.remitted)}
            </span>
          ) : (
            <span className='mono'>{inr(r.remitted)}</span>
          )
        ) : (
          <span className='muted'>—</span>
        ),
      exportValue: (r) => r.remitted,
    },
    {
      key: 'utr',
      label: 'UTR',
      render: (r) =>
        r.utr ? (
          <>
            <span className='mono' style={{fontSize: 12}}>
              {r.utr}
            </span>
            {r._utrVerified ? (
              <>
                {' '}
                <MiniPill tone='green'>UTR ✓</MiniPill>
              </>
            ) : null}
          </>
        ) : (
          <span className='muted'>—</span>
        ),
    },
    {
      key: 'receivedOn',
      label: 'Received On',
      render: (r) => (r.receivedOn ? fmtDate(r.receivedOn) : <span className='muted'>—</span>),
      value: (r) => r.receivedOn || '',
    },
  ]

  const filters = [
    {
      key: 'courier',
      label: 'Courier (multi-select · leave empty = All couriers)',
      options: courierSet.map((c) => ({value: c, label: c})),
      predicate: (r, sel) => sel.has(r.courier),
    },
    {
      key: 'codStatus',
      label: 'COD Status',
      options: COD_STATUS_OPTS.map((s) => ({value: s, label: s})),
      predicate: (r, sel) => sel.has(codStatus(r)),
    },
    {
      key: 'statusMatch',
      label: 'Status Match',
      options: [
        {value: 'Match', label: 'Match'},
        {value: 'Mismatch', label: 'Mismatch'},
      ],
      predicate: (r, sel) => sel.has(r.statusMatch),
    },
    {key: 'codRange', label: 'COD value range', type: 'range', field: 'codValue', unit: '₹'},
  ]

  const rowActions = (r) => [
    {
      label: 'View derivation',
      icon: <VisibilityOutlinedIcon fontSize='small' />,
      onClick: () => setDeriv(r),
    },
    {
      label: 'Raise Dispute',
      icon: <AssignmentLateOutlinedIcon fontSize='small' />,
      onClick: () => setDrawer({open: true, rows: [r]}),
    },
    {sep: true},
    {
      label: 'Mark Received',
      icon: <CheckIcon fontSize='small' />,
      hidden: r.courierStatus === 'Received',
      onClick: () => doMarkReceived([r]),
    },
  ]

  const bulkActions = [
    {
      label: 'Mark Received',
      icon: <CheckIcon fontSize='small' />,
      confirm: false,
      onClick: (sel, clear) => {
        const targets = sel.filter((r) => r.courierStatus !== 'Received')
        if (!targets.length) {
          toast('No eligible rows (already received).', 'error')
          return
        }
        doMarkReceived(targets, clear)
      },
    },
    {
      label: 'Raise Disputes',
      icon: <AssignmentLateOutlinedIcon fontSize='small' />,
      confirm: false,
      onClick: (sel, clear) => setDrawer({open: true, rows: sel, clear}),
    },
    {
      label: 'Verify UTRs',
      icon: <CheckCircleOutlineIcon fontSize='small' />,
      confirm: false,
      onClick: (sel, clear) => {
        const targets = sel.filter((r) => r.utr)
        if (!targets.length) {
          toast('No selected rows have a UTR to verify.', 'error')
          return
        }
        targets.forEach((r) => {
          r._utrVerified = true
        })
        refresh()
        clear()
        toast(targets.length + ' UTRs verified against bank (preview).', 'success')
      },
    },
    {
      label: 'Export Selected',
      icon: <DownloadOutlinedIcon fontSize='small' />,
      confirm: false,
      onClick: (sel, clear) => {
        const cols = [
          {label: 'AWB', value: (r) => r.awb},
          {label: 'Courier', value: (r) => r.courier},
          {label: 'COD Value', value: (r) => r.codValue},
          {
            label: 'Expected Remittance',
            value: (r) => {
              const er = expectedRemittance(r)
              return er.missing ? 'MISSING' : fmtDate(er.date)
            },
          },
          {label: 'COD Status', value: (r) => codStatus(r)},
          {label: 'Status Match', value: (r) => r.statusMatch},
        ]
        download(
          'cod-reconciliation-selected_' + TODAY.toISOString().slice(0, 10) + '.csv',
          toCsv(sel, cols)
        )
        toast('Exported ' + sel.length + ' selected row(s).', 'success')
        clear()
      },
    },
  ]

  const onlyPending = () => {
    setPreset({statuses: ['Due', 'Overdue'], courier: null})
    toast('Only Pending preset applied (Courier COD Due = true).', 'info')
  }

  const presetLabel = preset
    ? 'COD Status: ' +
      (preset.statuses || []).join(' / ') +
      (preset.courier && preset.courier !== 'All' ? ' · Courier: ' + preset.courier : '')
    : null

  return (
    <div>
      <div className='seclabel' style={{margin: '2px 0 10px'}}>
        Reconciliation Workspace — upgraded COD Details grid
      </div>
      <div className='warnbox info' style={{marginBottom: 14}}>
        <span style={{display: 'flex'}}>
          <InfoOutlinedIcon style={{width: 16, height: 16}} />
        </span>
        <div>
          Courier selector supports multi-select with explicit <b>All couriers</b>.{' '}
          <b>Expected Remittance Date</b> is computed from Cycle Config (hover to see the
          derivation). <b>COD Status</b> and <b>Status Match</b> are computed; the legacy Courier Due
          Date is shown for reference only.
        </div>
      </div>

      {preset ? (
        <div className='row gap8' style={{marginBottom: 12, alignItems: 'center'}}>
          <span className='muted' style={{fontSize: 12}}>
            Preset applied:
          </span>
          <span className='tag'>{presetLabel}</span>
          <button className='btn btn-sm btn-ghost' onClick={() => setPreset(null)}>
            <CloseIcon style={{width: 14, height: 14}} />
            Clear preset
          </button>
        </div>
      ) : null}

      <SupplyTable
        rows={rows}
        rowKey={(r) => r.awb}
        columns={columns}
        searchKeys={['awb', 'ref', 'cpCode', 'courier', 'merchant']}
        searchPlaceholder='Search AWB, reference, code, courier, merchant...'
        defaultSort={{key: 'delivered', dir: 'desc'}}
        exportName='cod-reconciliation'
        emptyText='No COD shipments in scope.'
        filters={filters}
        rowActions={rowActions}
        bulkActions={bulkActions}
        toolbarRight={
          <button className='btn' onClick={onlyPending}>
            <FilterListIcon style={{width: 15, height: 15}} />
            Only Pending
          </button>
        }
      />

      <RaiseDisputeDrawer
        open={drawer.open}
        rows={drawer.rows}
        onClose={() => setDrawer({open: false, rows: []})}
        onDone={() => {
          if (drawer.clear) drawer.clear()
          refresh()
        }}
      />

      <Modal
        open={!!deriv}
        onClose={() => setDeriv(null)}
        title={deriv ? 'Expected Remittance Date — ' + deriv.awb : ''}
        icon={<CalendarMonthOutlinedIcon fontSize='small' />}
        width={520}
        actions={[{label: 'Close', onClick: () => setDeriv(null)}]}
      >
        {deriv ? (
          <>
            <p style={{fontSize: 13, lineHeight: 1.7}}>{expectedRemittance(deriv).deriv}</p>
            <div className='warnbox info' style={{marginTop: 10}}>
              <span style={{display: 'flex'}}>
                <InfoOutlinedIcon style={{width: 16, height: 16}} />
              </span>
              <div>
                Computed from M6.7 Cycle Config (cycle D+N, settlement weekdays, daily cut-off).
                Historical AWBs use the config active on their delivered date.
              </div>
            </div>
          </>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? confirm.title : ''}
        body={confirm ? confirm.body : ''}
        bullets={confirm ? confirm.bullets : undefined}
        needReason={confirm ? confirm.needReason : false}
        okLabel={confirm ? confirm.okLabel : ''}
        onOk={(reason) => {
          if (confirm) confirm.onOk(reason)
          setConfirm(null)
        }}
      />
    </div>
  )
}
WorkspaceTab.propTypes = {
  statusSeed: PropTypes.array,
  courierSeed: PropTypes.string,
}
